// Dashboard.js
import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { GET_DASHBOARD_AGGREGATE } from './queries/dashboardQuery';

import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Legend, LineChart, Line } from 'recharts';
import { Card, CardContent, Typography, Grid, Box } from '@mui/material';

import {
  groupByMonthDate,
  mapAccidentPeriod,
  groupByWorkflowSubcategory
} from "./utils";

import {
  subcategoryToReasonAccident,
  subcategoryToReasonTraning,
  subcategoryToReasonServey
} from "./subcategoryMappers";

const COLORS = [
  '#FFC107', '#E53935', '#1E88E5', '#43A047', '#E91E63', '#9C27B0',
  '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688',
  '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800',
  '#FF5722', '#795548', '#9E9E9E', '#607D8B', '#212121'
];

const InfoCard = ({ title, value }) => (
  <Card sx={{ backgroundColor: '#305175', color: 'white', textAlign: 'center', py: 1, borderRadius: 3, boxShadow: 4 }}>
    <CardContent>
      <Typography variant="h6" sx={{ color: '#FFBB28' }}>{title}</Typography>
      <Typography variant="h2" sx={{ fontWeight: 'bold' }}>{value}</Typography>
    </CardContent>
  </Card>
);

const SimpleBarChart = ({ title, data, colors }) => (
  <Card sx={{ backgroundColor: '#305175', color: 'white', p: 1 }}>
    <CardContent style={{ direction: 'rtl', textAlign: 'right' }}>
      <Typography variant="h6">{title}</Typography>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
          <XAxis dataKey="reason" stroke="#ffffff" style={{ fontSize: '14px' }} />
          <YAxis stroke="#ffffff" style={{ fontSize: '14px' }} />
          <Tooltip />
          <Bar dataKey="count" fill="#8884d8">
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

const BarChartCard = ({ title, data }) => (
  <Card sx={{ backgroundColor: '#305175', color: 'white', p: 1 }}>
    <CardContent>
      <Typography variant="h6">{title}</Typography>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data}>
          <XAxis dataKey="week" stroke="white" />
          <YAxis stroke="white" />
          <Legend />
          <Tooltip />
          <Bar dataKey="students" name="بوجود طلاب" fill="#F44336" />
          <Bar dataKey="nonStudents" name="بدون طلاب" fill="#4CAF50" />
        </BarChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

const LineChartCard = ({ title, data }) => (
  <Card sx={{ backgroundColor: '#305175', color: 'white', p: 2 }}>
    <CardContent>
      <Typography variant="h6">{title}</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <XAxis dataKey="week" stroke="white" />
          <YAxis stroke="white" />
          <Tooltip />
          <Line type="monotone" dataKey="incidents" stroke="#0088FE" name="سائق مدرب" />
        </LineChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { data, loading, error } = useQuery(GET_DASHBOARD_AGGREGATE);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  const {
    dashboard_training_per_day = [],
    dashboard_accident_per_period = [],
    dashboard_accident_per_status = [],
    dashboard_traning_per_status = [],
    dashboard_survey_per_status = [],
    traning_courses, traning_drivers, pending_survey, done_survey,
    accidents_without_students, accidents_with_students
  } = data;

  const chartData = groupByMonthDate(dashboard_training_per_day);
  const chartDataAccident = mapAccidentPeriod(dashboard_accident_per_period);
  const chartDataAccidentStatus = groupByWorkflowSubcategory(dashboard_accident_per_status, subcategoryToReasonAccident);
  const chartDataTraningStatus = groupByWorkflowSubcategory(dashboard_traning_per_status, subcategoryToReasonTraning);
  const chartDataSurveyStatus = groupByWorkflowSubcategory(dashboard_survey_per_status, subcategoryToReasonServey);

  const infoCardsData = [
    { title: 'الدورات التدريبية', value: traning_courses?.aggregate?.count || 0 },
    { title: 'السائقين المدربين', value: traning_drivers?.aggregate?.count || 0 },
    { title: 'أستبيانات لم تتم', value: pending_survey?.aggregate?.count || 0 },
    { title: 'استبيانات تمت', value: done_survey?.aggregate?.count || 0 },
    { title: 'حوادث بلا طلاب', value: accidents_without_students?.aggregate?.count || 0 },
    { title: 'حوادث ب طلاب', value: accidents_with_students?.aggregate?.count || 0 }
  ];

  return (
    <Box sx={{ backgroundColor: "#EEF2F6", color: "#1E293B", p: 4, borderRadius: 2, minHeight: "100vh" }}>
      <Grid container spacing={2}>
        {infoCardsData.map((info, index) => (
          <Grid item key={index} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <InfoCard {...info} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {[{ title: 'الدورات التدريبية', data: chartDataTraningStatus, colors: COLORS }, { title: 'الاستبيانات', data: chartDataSurveyStatus, colors: COLORS }, { title: 'الحوادث', data: chartDataAccidentStatus, colors: COLORS }].map((chartProps, i) => (
          <Grid item key={i} size={{ xs: 12, md: 6, lg: 4 }}>
            <SimpleBarChart {...chartProps} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item size={{ xs: 12 }}>
          <BarChartCard title="الحوادث بالفترات التشغيلية" data={chartDataAccident} />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <LineChartCard title="السائقين المدربين" data={chartData} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
